import React, {createContext, useContext, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import * as _ from 'lodash';
import {FormikProvider, useFormik} from 'formik';
import {formattingPrice} from '../../_common/utils/format';
import * as columnFormatters from '../../_common/column-formatters';
import {monthsOptions, splitsOptions} from '../../_common/constants/options';
import * as actions from '../_redux/transfersByAgentActions';
import {getPaymentTypeName, getYearsOptions, isNewPaycheck, getInitialAmountPercent} from '../utils';
import {useTransfersByAgentContext} from './TransfersByAgentContext';

const PopupViewTransferContext = createContext({});
export const usePopupViewTransferContext = () => useContext(PopupViewTransferContext);

export const PopupViewTransferContextProvider = ({children, cellContent}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const {currentLastPaycheck} = useTransfersByAgentContext();
  const getTransferData = () => dispatch(actions.getTransfer(cellContent._id));
  const {data} = useSelector((state) => ({data: state.transfer.data}), shallowEqual);

  const columns = useMemo(() => [
    {
      accessor: 'checkbox',
      Header: ''
    },
    {
      accessor: 'receiptID',
      Header: intl.formatMessage({
        id: 'transfersByAgent-transfers-column-receiptID',
        defaultMessage: 'Receipt ID'
      })
    },
    {
      accessor: 'customer',
      Header: intl.formatMessage({
        id: 'transfersByAgent-transfers-column-customer',
        defaultMessage: 'Customer'
      })
    },
    {
      accessor: 'receiptDate',
      Header: intl.formatMessage({
        id: 'transfersByAgent-transfers-column-receiptDate',
        defaultMessage: 'Receipt date'
      })
    },
    {
      accessor: 'paymentType',
      Header: intl.formatMessage({
        id: 'transfersByAgent-transfers-column-paymentType',
        defaultMessage: 'Payment type'
      })
    },
    {
      accessor: 'amount',
      Header: intl.formatMessage({
        id: 'transfersByAgent-transfers-column-amount',
        defaultMessage: 'Amount'
      })
    },
    {
      accessor: 'vat',
      Header: intl.formatMessage({
        id: 'transfersByAgent-transfers-column-vat',
        defaultMessage: 'VAT'
      })
    },
  ], [intl]);
  const lines = _.get(data, 'lines', []);
  const vatIncludedLineIds = _.get(data, 'vatIncludedLineIds', []);
  const mappedLines = lines.map(item => {
    return {vatIncluded: vatIncludedLineIds.includes(item._id), ...item};
  });
  const tableData = useMemo(() => [
    ...mappedLines.map((item, index) => {
      const customer = [
        _.get(item, 'receipt.customer._id', ''),
        '-',
        _.get(item, 'receipt.customer.name.first', ''),
        _.get(item, 'receipt.customer.name.last', '')
      ].filter(Boolean).join(' ');
      const amount = item.amount && typeof item.amount === 'number' ? item.amount.toFixed(2) : item.amount;
      const amountValue = formattingPrice(amount, true);
      return {
        _id: item._id,
        checkbox: columnFormatters.TransfersCheckboxColumnFormatter(`table[${index}]receipt-${item._id}`, true),
        receiptID: columnFormatters.TransfersNormalColumnFormatter(item._id),
        customer: columnFormatters.TransfersNormalColumnFormatter(customer),
        receiptDate: columnFormatters.TransfersDateColumnFormatter(item.dueDate),
        paymentType: columnFormatters.TransfersNormalColumnFormatter(getPaymentTypeName(item.paymentType)),
        amount: columnFormatters.TransfersNormalColumnFormatter(amountValue),
        vat: columnFormatters.TransfersCheckboxColumnFormatter(`table[${index}]receipt-${item._id}-vatIncluded`, true),
      };
    })
  ], [mappedLines]);

  const customLines = Boolean(data?.customLines) ? data.customLines : [];
  const vatIncludedCustomLines = _.get(data, 'vatIncludedCustomLines', {});
  const mappedCustomLines = customLines.map((item, index) => {
    return {vatIncluded: Boolean(vatIncludedCustomLines[`${index}`]), ...item};
  });

  const getValue = (name) => {
    return data?.calculation[`${name}`] ? Math.abs(data.calculation[`${name}`]) : 0;
  };

  const parameters = {
    year: data?.paycheck.date
      ? getYearsOptions().filter(item => item.value === data?.paycheck.date.year) : [],
    month: data?.paycheck.date
      ? monthsOptions.filter(item => item.value === data?.paycheck.date.month) : [],
    splits: data?.paycheck
      ? splitsOptions.filter(item => item.value === data?.paycheck.splits) : [],
  };

  const initialValues = {
    expensesWithVat: _.get(data, 'transferParams.expensesWithVat', 0),
    vat: getValue('vat'),
    expensesWithoutVat: _.get(data, 'transferParams.expensesWithoutVat', 0),
    exchangeFee: getValue('exchangeFee'),
    pensionManagementCommission: _.get(data, 'socialParams.pensionManagementCommission', 0),
    incomeTaxOffset: getValue('incomeTaxOffset'),

    commission: getInitialAmountPercent(data, 'socialParams.commission', 'commission').value,
    commissionValueType: getInitialAmountPercent(data, 'socialParams.commission', 'commission').type,
    creditCardCommission: getInitialAmountPercent(data, 'transferParams.creditCardCommission', 'creditCardCommission').value,
    creditCardCommissionValueType: getInitialAmountPercent(data, 'transferParams.creditCardCommission', 'creditCardCommission').type,
    compensations: getInitialAmountPercent(data, 'socialParams.compensations', 'compensations').value,
    compensationsValueType: getInitialAmountPercent(data, 'socialParams.compensations', 'compensations').type,
    employerEducationFund: getInitialAmountPercent(data, 'socialParams.employerEducationFund', 'employerEducationFund').value,
    employerEducationFundValueType: getInitialAmountPercent(data, 'socialParams.employerEducationFund', 'employerEducationFund').type,
    employerProvidentFund: getInitialAmountPercent(data, 'socialParams.employerProvidentFund', 'employerProvidentFund').value,
    employerProvidentFundValueType: getInitialAmountPercent(data, 'socialParams.employerProvidentFund', 'employerProvidentFund').type,
    employerMandatoryPension: getInitialAmountPercent(data, 'socialParams.employerMandatoryPension', 'employerMandatoryPension').value,
    employerMandatoryPensionValueType: getInitialAmountPercent(data, 'socialParams.employerMandatoryPension', 'employerMandatoryPension').type,
    employeeEducationFund: getInitialAmountPercent(data, 'socialParams.employeeEducationFund', 'employeeEducationFund').value,
    employeeEducationFundValueType: getInitialAmountPercent(data, 'socialParams.employeeEducationFund', 'employeeEducationFund').type,
    employeeProvidentFund: getInitialAmountPercent(data, 'socialParams.employeeProvidentFund', 'employeeProvidentFund').value,
    employeeProvidentFundValueType: getInitialAmountPercent(data, 'socialParams.employeeProvidentFund', 'employeeProvidentFund').type,
    employeeMandatoryPension: getInitialAmountPercent(data, 'socialParams.employeeMandatoryPension', 'employeeMandatoryPension').value,
    employeeMandatoryPensionValueType: getInitialAmountPercent(data, 'socialParams.employeeMandatoryPension', 'employeeMandatoryPension').type,

    advancePayment: getValue('advancePayment'),
    nationalInsurance: _.get(data, 'socialParams.nationalInsurance', 0),
    nationalInsuranceDebt: getValue('nationalInsuranceDebt'),
    supplierExpensesRefund: getValue('supplierExpensesRefund'),
    additionalCommission: getValue('additionalCommission'),
    year: parameters.year.length > 0 ? parameters.year[0] : 0,
    month: parameters.month.length > 0 ? parameters.month[0] : 0,
    splits: parameters.splits.length > 0 ? parameters.splits[0] : 1,
    table: mappedLines.map((item) => {
      return {
        _id: item._id,
        [`receipt-${item._id}`]: true,
        [`receipt-${item._id}-vatIncluded`]: item.vatIncluded,
        amount: item.amount
      };
    }),
    customLines: mappedCustomLines.map(item => {
      return {
        customer: item.customer ? item.customer : '',
        creationDate: item.creationDate ? item.creationDate : '',
        paymentType: item.paymentType ? item.paymentType : '',
        amount: item.amount ? item.amount : 0,
        vatIncluded: item.vatIncluded
      };
    }),
    adminNotes: _.get(data, 'preferences.adminNotes') || '',
    allReceiptsChecked: true
  };

  const formik = useFormik({
    initialValues: {...initialValues},
    onSubmit: () => {
    },
    validateOnMount: true,
    enableReinitialize: true,
  });

  const updateVat = vat => formik.setFieldValue('vat', vat);

  return (
    <PopupViewTransferContext.Provider
      value={{
        formik,
        getTransferData,
        columns,
        tableData,
        cellContent,
        data,
        getYearsOptions,
        newPaycheck: isNewPaycheck(formik.values, currentLastPaycheck),
        updateVat
      }}
    >
      <FormikProvider value={formik}>{children}</FormikProvider>
    </PopupViewTransferContext.Provider>
  );
};